/**
 * Create Order Dialog — multi-step "Nouvelle commande" form.
 *
 * Extracted from OrdersDialogs.jsx. This is a fully controlled component:
 * the parent (OrdersTab) owns all the form state (orderForm, currentItem)
 * and the business logic (handleAddItem, handleRemoveItem, handleCreateOrder,
 * calculateTotal, calculateLineTotal, getBillingInfo, getOrderModeLabel,
 * getSupplierProductCategory, availableSupplierProducts, getAllowedSites).
 *
 * The data-testids preserved verbatim for test stability:
 *   order-supplier-select, order-site-select, item-product-select,
 *   item-quantity-input, add-item-btn, order-notes-input,
 *   confirm-create-order-btn.
 */
import React from 'react';
import { Plus, Trash2, ZoomIn, ZoomOut } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { useComfortMode } from '@/hooks/useComfortMode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import HideAddedToggle from '../components/HideAddedToggle';
import OrderableModeToggle from '../components/OrderableModeToggle';
import CategorizedProductSelectContent from '../components/CategorizedProductSelectContent';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export default function CreateOrderDialog({
    // dialog open state
    open,
    onOpenChange,
    // form state
    orderForm,
    setOrderForm,
    currentItem,
    setCurrentItem,
    // data
    suppliers,
    sites,
    supplierProducts,
    availableSupplierProducts,
    // helpers
    getAllowedSites,
    getSupplierProductCategory,
    getBillingInfo,
    getOrderModeLabel,
    calculateLineTotal,
    calculateTotal,
    // handlers
    onAddItem,
    onRemoveItem,
    onCreateOrder,
    // toggle: hide products already added
    hideAddedProducts,
    setHideAddedProducts,
    // toggle: order-mode (admin/supervisor only) — 'normal' vs 'non_commandable'
    orderableMode,
    setOrderableMode,
}) {
    const { comfort, toggleComfort } = useComfortMode();
    const cellPad = comfort ? 'py-3.5' : 'py-2';
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[95vw] sm:w-full sm:h-[95vh] overflow-hidden flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <div className="flex items-center justify-between gap-3">
                        <DialogTitle>Nouvelle commande</DialogTitle>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant={comfort ? 'default' : 'outline'}
                                size="sm"
                                onClick={toggleComfort}
                                className={comfort ? 'bg-indigo-600 hover:bg-indigo-700' : 'text-slate-500'}
                                title={comfort ? 'Désactiver le mode confort' : 'Activer le mode confort (affichage agrandi)'}
                                data-testid="comfort-mode-toggle"
                            >
                                {comfort ? <ZoomOut className="w-4 h-4 mr-1" /> : <ZoomIn className="w-4 h-4 mr-1" />}
                                Confort
                            </Button>
                            <OrderableModeToggle
                                mode={orderableMode}
                                onChange={setOrderableMode}
                            />
                        </div>
                    </div>
                </DialogHeader>

                {/* Header - Fournisseur & Site & Date */}
                <div className="flex-shrink-0 grid grid-cols-2 sm:grid-cols-3 gap-4 py-4 border-b">
                    <div className="space-y-2">
                        <Label>Fournisseur *</Label>
                        <Select
                            value={orderForm.supplier_id}
                            onValueChange={(value) => setOrderForm({ ...orderForm, supplier_id: value, items: [] })}
                        >
                            <SelectTrigger data-testid="order-supplier-select">
                                <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                                {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })).map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Site de destination *</Label>
                        <Select
                            value={orderForm.site_id}
                            onValueChange={(value) => setOrderForm({ ...orderForm, site_id: value })}
                        >
                            <SelectTrigger data-testid="order-site-select">
                                <SelectValue placeholder="Sélectionner" />
                            </SelectTrigger>
                            <SelectContent>
                                {getAllowedSites(sites).map(s => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Date de commande</Label>
                        <Input
                            type="date"
                            value={orderForm.order_date || ''}
                            onChange={(e) => setOrderForm({ ...orderForm, order_date: e.target.value })}
                            data-testid="order-date-input"
                        />
                        <p className="text-[11px] text-slate-400 leading-tight">Détermine la semaine de consolidation.</p>
                    </div>
                </div>

                {/* Fixed - Add product section */}
                {orderForm.supplier_id && (
                    <div className="flex-shrink-0 border-b py-2">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                                <div className="flex items-center gap-3">
                                    <Label className="whitespace-nowrap shrink-0 font-semibold">Ajouter produit :</Label>
                                    <Select
                                        value={currentItem.supplier_product_id}
                                        onValueChange={(value) => setCurrentItem({ ...currentItem, supplier_product_id: value })}
                                    >
                                        <SelectTrigger data-testid="item-product-select" className={`flex-1 min-w-0 ${comfort ? 'h-12 text-base' : ''}`}>
                                            <SelectValue placeholder="Sélectionner un produit" />
                                        </SelectTrigger>
                                        <CategorizedProductSelectContent
                                            products={availableSupplierProducts}
                                            getCategory={getSupplierProductCategory}
                                            comfort={comfort}
                                            renderLabel={(sp) => {
                                                const billingUnit = sp.billing_unit || 'pièce';
                                                const orderUnit = sp.order_unit || 'pièce';
                                                const packagingQty = sp.packaging_qty;
                                                const priceDisplay = packagingQty && !sp.sell_by_detail
                                                    ? `${formatCurrency(sp.unit_price)}/${billingUnit} - ${orderUnit} de ${packagingQty}${billingUnit}`
                                                    : `${formatCurrency(sp.unit_price)}/${billingUnit}`;
                                                return `${sp.supplier_name_product || sp.industrial_product_name} (${priceDisplay})`;
                                            }}
                                        />
                                    </Select>
                                    <HideAddedToggle
                                        checked={hideAddedProducts}
                                        onCheckedChange={setHideAddedProducts}
                                        testid="hide-added-toggle-create-order"
                                    />
                                </div>
                            </div>

                            {/* Mode de commande et quantité */}
                            {currentItem.supplier_product_id && (() => {
                                const sp = supplierProducts.find(p => p.id === currentItem.supplier_product_id);
                                const hasPackaging = sp?.packaging_qty && sp.packaging_qty > 0;
                                const orderUnit = sp?.order_unit || 'pièce';
                                const billingUnit = sp?.billing_unit || 'pièce';
                                const packagingQty = sp?.packaging_qty;
                                const invoiceUnit = sp?.invoice_unit || billingUnit;
                                // Detail mode is incoherent when invoice and billing units differ:
                                // the qty would be in billing_unit but the price is per invoice_unit.
                                const detailModeAllowed = !hasPackaging || !invoiceUnit || invoiceUnit.toLowerCase() === billingUnit.toLowerCase();

                                const qtyBilled = currentItem.order_mode === 'detail' || !hasPackaging
                                    ? currentItem.quantity
                                    : currentItem.quantity * packagingQty;
                                const unitForQty = currentItem.order_mode === 'detail' ? billingUnit : orderUnit;

                                const invoicePerOrderUnit = hasPackaging
                                    && invoiceUnit.toLowerCase() === orderUnit.toLowerCase();
                                // Detail mode with mixed units → convert price per billing_unit
                                const detailMixedUnits = currentItem.order_mode === 'detail' && hasPackaging
                                    && billingUnit && invoiceUnit
                                    && billingUnit.toLowerCase() !== invoiceUnit.toLowerCase();
                                const lineTotal = detailMixedUnits
                                    ? currentItem.quantity * ((sp?.unit_price || 0) / packagingQty)
                                    : (currentItem.order_mode === 'detail' || !hasPackaging || invoicePerOrderUnit
                                        ? currentItem.quantity * (sp?.unit_price || 0)
                                        : currentItem.quantity * packagingQty * (sp?.unit_price || 0));

                                return (
                                    <div className="col-span-2 space-y-3">
                                        {hasPackaging && (
                                            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                                                <Label className="text-sm font-medium">Mode de commande :</Label>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={currentItem.order_mode === 'packaging' ? 'default' : 'outline'}
                                                        onClick={() => setCurrentItem({ ...currentItem, order_mode: 'packaging' })}
                                                        className={`${currentItem.order_mode === 'packaging' ? 'bg-blue-600' : ''} ${comfort ? 'h-11 px-4 text-sm' : ''}`}
                                                    >
                                                        Par colisage ({orderUnit})
                                                    </Button>
                                                    {detailModeAllowed && (
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant={currentItem.order_mode === 'detail' ? 'default' : 'outline'}
                                                            onClick={() => setCurrentItem({ ...currentItem, order_mode: 'detail' })}
                                                            className={`${currentItem.order_mode === 'detail' ? 'bg-amber-600' : ''} ${comfort ? 'h-11 px-4 text-sm' : ''}`}
                                                        >
                                                            Au détail ({billingUnit})
                                                        </Button>
                                                    )}
                                                </div>
                                                {!detailModeAllowed && (
                                                    <span className="text-xs text-slate-400 italic ml-2">
                                                        Mode détail indisponible : prix par {invoiceUnit}, colisage en {billingUnit}.
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Quantité ({unitForQty})</Label>
                                                <div className="flex gap-2">
                                                    {(() => {
                                                        const isVariableWeight = !!sp?.variable_weight && currentItem.order_mode !== 'packaging';
                                                        return (
                                                            <Input
                                                                type="number"
                                                                inputMode={isVariableWeight ? 'decimal' : 'numeric'}
                                                                min={isVariableWeight ? '0.01' : '1'}
                                                                step={isVariableWeight ? '0.01' : '1'}
                                                                value={currentItem.quantity}
                                                                onFocus={(e) => e.target.select()}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value;
                                                                    const parsed = isVariableWeight
                                                                        ? (parseFloat(raw) || 0)
                                                                        : (parseInt(raw) || 1);
                                                                    setCurrentItem({ ...currentItem, quantity: parsed });
                                                                }}
                                                                data-testid="item-quantity-input"
                                                                className={comfort ? 'h-12 text-lg' : ''}
                                                            />
                                                        );
                                                    })()}
                                                    <Button onClick={onAddItem} data-testid="add-item-btn" className={comfort ? 'h-12 w-16' : ''}>
                                                        <Plus className={comfort ? 'w-6 h-6' : 'w-4 h-4'} />
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="p-3 bg-emerald-50 rounded-lg">
                                                <div className="text-xs text-emerald-600 font-medium mb-1">Facturation prévue :</div>
                                                <div className="text-lg font-bold text-emerald-700">
                                                    {qtyBilled} {currentItem.order_mode === 'detail' ? billingUnit : (hasPackaging ? billingUnit : invoiceUnit)}
                                                </div>
                                                <div className="text-xs text-emerald-600">
                                                    à {formatCurrency(sp?.unit_price || 0)}/{invoiceUnit} = {formatCurrency(lineTotal)}
                                                </div>
                                            </div>
                                        </div>

                                        {hasPackaging && currentItem.order_mode === 'packaging' && (
                                            <div className="text-xs text-slate-500 italic">
                                                1 {orderUnit} = {packagingQty} {billingUnit}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {!currentItem.supplier_product_id && (
                                <div className="space-y-2">
                                    <Label>Quantité</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            min="1"
                                            value={currentItem.quantity}
                                            onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 1 })}
                                            data-testid="item-quantity-input"
                                            disabled
                                        />
                                        <Button onClick={onAddItem} data-testid="add-item-btn" disabled>
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Scrollable Products List */}
                <div className="flex-1 overflow-auto py-4 min-h-0">
                    {orderForm.items.length > 0 && (
                        <div className="border rounded-lg p-4">
                            <table className={`w-full ${comfort ? 'text-base' : 'text-sm'}`}>
                                <thead>
                                    <tr className="text-left text-slate-500 border-b">
                                        <th className="pb-2">Réf.</th>
                                        <th className="pb-2">Produit</th>
                                        <th className="pb-2 text-center">Mode</th>
                                        <th className="pb-2 text-right">P.U.</th>
                                        <th className="pb-2 text-center">Qté commandée</th>
                                        <th className="pb-2 text-center">Qté à facturer</th>
                                        <th className="pb-2 text-right">Total</th>
                                        <th className="pb-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderForm.items.map((item, index) => {
                                        const billingInfo = getBillingInfo(item);
                                        const modeInfo = getOrderModeLabel(item);
                                        return (
                                            <tr key={index} className="border-b last:border-0">
                                                <td className={`${cellPad} font-mono text-xs`}>{item.supplier_ref}</td>
                                                <td className={cellPad}>{item.product_name}</td>
                                                <td className={`${cellPad} text-center`}>
                                                    <Badge variant="secondary" className={modeInfo.className}>
                                                        {modeInfo.label}
                                                    </Badge>
                                                </td>
                                                <td className={`${cellPad} text-right ${comfort ? 'text-sm' : 'text-xs'}`}>{billingInfo.priceLabel}</td>
                                                <td className={`${cellPad} text-center`}>
                                                    <span className={`inline-flex items-center rounded bg-blue-100 text-blue-800 font-semibold ${comfort ? 'px-3 py-1.5' : 'px-2 py-1'}`}>
                                                        {billingInfo.qtyOrdered}
                                                    </span>
                                                </td>
                                                <td className={`${cellPad} text-center`}>
                                                    <span className={`inline-flex items-center rounded bg-emerald-100 text-emerald-800 font-semibold ${comfort ? 'px-3 py-1.5' : 'px-2 py-1'}`}>
                                                        {billingInfo.qtyBilled}
                                                    </span>
                                                </td>
                                                <td className={`${cellPad} text-right font-semibold`}>{formatCurrency(calculateLineTotal(item))}</td>
                                                <td className={`${cellPad} text-right`}>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className={`text-red-500 ${comfort ? 'h-11 w-11 p-0' : ''}`}
                                                        onClick={() => onRemoveItem(index)}
                                                    >
                                                        <Trash2 className={comfort ? 'w-5 h-5' : 'w-3 h-3'} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="font-semibold">
                                        <td colSpan="6" className="pt-4 text-right">Total:</td>
                                        <td className="pt-4 text-right">{formatCurrency(calculateTotal())}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                    {orderForm.items.length === 0 && orderForm.supplier_id && (
                        <div className="text-center py-8 text-slate-400">
                            Aucun produit ajouté
                        </div>
                    )}
                </div>

                {/* Footer - Notes & Buttons */}
                <div className="flex-shrink-0 border-t pt-4 space-y-4">
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Input
                            value={orderForm.notes}
                            onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                            placeholder="Notes optionnelles"
                            data-testid="order-notes-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Annuler
                        </Button>
                        <Button onClick={onCreateOrder} className={`btn-primary ${comfort ? 'h-12 px-6 text-base' : ''}`} data-testid="confirm-create-order-btn">
                            Créer la commande
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
}
